import React, { useEffect, useRef } from 'react';

import { Formik } from 'formik';
import * as Yup from 'yup';

import { toDateString, toLocalDate } from 'Helpers/dateFormat';
import { modalOptions } from 'Helpers/modalOptions';

import { FormikDatePickerField } from 'CommonComponents/FormikDatePickerField';
import Modal from 'CommonComponents/Modal';
import ModalFooter from 'CommonComponents/ModalFooter';
import ModalHeader from 'CommonComponents/ModalHeader';
import TextFormField from 'CommonComponents/TextFormField';

import useModal from 'Hooks/useModal';
import { useToast } from 'Hooks/useToast';

const VersionSchema = Yup.object().shape({
    name: Yup.string().required($t('Version name is required')),
    starts_on: Yup.date().typeError($t('Choose a start date')).required($t('Start date is required')),
    ends_on: Yup.date()
        .typeError($t('Choose an end date'))
        .required($t('End date is required'))
        .min(Yup.ref('starts_on'), $t('The end date cannot fall before the start date')),
});

export default function RateCardVersionModal({ version, cloneFromVersion, storeUrl, updateUrl, onSaved, onClose }) {
    const modal = useModal({ options: { ...modalOptions(false), onHide: onClose }, visible: true });
    const toast = useToast();

    const modalStateRef = useRef(modal);
    modalStateRef.current = modal;

    useEffect(
        () => () => {
            const { element } = modalStateRef.current;

            if (element && element.isVisible()) {
                element.hide();
            }
        },
        [],
    );

    const close = () => modal.hide();
    const isEdit = !!version;

    const initialValues = {
        name: version?.name ?? '',
        starts_on: toLocalDate(version?.starts_on),
        ends_on: toLocalDate(version?.ends_on),
    };

    return (
        <Modal id="rate-card-version-modal" modal={modal}>
            <Formik
                initialValues={initialValues}
                validationSchema={VersionSchema}
                onSubmit={(values, actions) => {
                    const payload = {
                        name: values.name,
                        starts_on: toDateString(values.starts_on),
                        ends_on: toDateString(values.ends_on),
                    };

                    if (!isEdit && cloneFromVersion) {
                        payload.clone_from_version_id = cloneFromVersion.id;
                    }

                    const request = isEdit ? axios.put(updateUrl(version.id), payload) : axios.post(storeUrl, payload);

                    return request
                        .then(({ data }) => {
                            close();
                            onSaved(data?.data ?? null);
                        })
                        .catch((e) => {
                            if (e.response?.data?.errors) {
                                actions.setErrors(e.response.data.errors);
                            } else {
                                toast.error($t('Something went wrong'));
                            }
                        })
                        .finally(() => {
                            actions.setSubmitting(false);
                        });
                }}
            >
                {({ errors, touched, isSubmitting, handleSubmit }) => (
                    <>
                        <ModalHeader title={isEdit ? $t('Edit version') : $t('New version')} close={close} />

                        <div className="modal__form-container">
                            {!isEdit && cloneFromVersion && (
                                <p className="tw-text-sm tw-text-charcoal-75 tw-mb-0">
                                    {$t('The active roles on {{version}} are copied across at their current rates. Archived roles stay behind.', {
                                        version: cloneFromVersion.name,
                                    })}
                                </p>
                            )}

                            <TextFormField label={$t('Version name')} name="name" type="text" required />

                            <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2">
                                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                    <label htmlFor="starts_on" className="text-input__label">
                                        {$t('Start date')} *
                                    </label>
                                    {/* Opens below the field where the viewport allows, and
                                        flips up rather than off-screen where it does not. */}
                                    <FormikDatePickerField name="starts_on" className="text-input" popperPlacement="bottom-start" />
                                    {touched.starts_on && errors.starts_on ? <div className="tw-text-red-500 tw-text-sm">{errors.starts_on}</div> : null}
                                </div>

                                <div className="tw-flex tw-flex-col tw-space-y-1.5">
                                    <label htmlFor="ends_on" className="text-input__label">
                                        {$t('End date')} *
                                    </label>
                                    <FormikDatePickerField name="ends_on" className="text-input" popperPlacement="bottom-start" />
                                    {touched.ends_on && errors.ends_on ? <div className="tw-text-red-500 tw-text-sm">{errors.ends_on}</div> : null}
                                </div>
                            </div>

                            <p className="tw-text-sm tw-text-charcoal-50 tw-mb-0">
                                {$t('Both dates count as part of the version. Two versions cannot share a day.')}
                            </p>

                            <ModalFooter
                                cancelButtonText={$t('Cancel')}
                                primaryButtonText={$t('Save')}
                                submitButtonVariant="primary"
                                onCancel={close}
                                handleSubmit={handleSubmit}
                                isSubmitting={isSubmitting}
                            />
                        </div>
                    </>
                )}
            </Formik>
        </Modal>
    );
}
